import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL;
const API_KEY = process.env.REACT_APP_API_KEY;

// Populated at app startup from getStartPoint(), which resolves the dealer
// user id, website id, and approval status for the site currently being
// served (see App.js).
const siteConfig = {
  userId: null,
  websiteId: null,
  requestStatus: null,
};

// Reads ?preview=1&website_id=X from the current URL (used by the dealer admin's
// "Preview" button to show draft/unpublished content). Both must be present -
// otherwise the default website_id keeps being used.
function getPreviewOverrides() {
  const params = new URLSearchParams(window.location.search);
  const isPreview = ['1', 'true', 'yes'].includes((params.get('preview') || '').toLowerCase());
  const websiteId = params.get('website_id');

  return isPreview && websiteId ? { preview: 1, website_id: websiteId } : null;
}

export class ServiceWrapper {

  get defaultParams() {
    return {
      user_id: siteConfig.userId,
      website_id: siteConfig.websiteId,
      ...getPreviewOverrides(),
    };
  }

  // Resolves the dealer user id and website id for the current host. Called
  // once at app startup, before any other request needs those defaults.
  getStartPoint = () =>
    axios.get(`${API_BASE_URL}/start-point`, {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
      },
      params: { slug: window.location.host },
    }).then((r) => r.data);

  setSiteConfig = ({ user_id, website }) => {
    siteConfig.userId = user_id;
    siteConfig.websiteId = website?.id;
    siteConfig.requestStatus = website?.request_status;
  };

  isApproved = () => siteConfig.requestStatus === 'approved';

  // Every API call (other than getStartPoint, which is what determines this
  // status in the first place) is gated on the site's approval status so an
  // unapproved/unknown site can't pull data through any endpoint.
  callGet = async (url, params = {}) => {
    if (!this.isApproved()) {
      return Promise.reject(new Error('Site request is not approved'));
    }
    return await axios.get(API_BASE_URL + url, {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
      },
      params: { ...this.defaultParams, ...params },
    });
  };

  callPost = async (url, body) => {
    if (!this.isApproved()) {
      return Promise.reject(new Error('Site request is not approved'));
    }
    return await axios.post(API_BASE_URL + url, body, {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
      },
    });
  };

  // ─── API methods ────────────────────────────────────────────────────────────

  getNavigation = () =>
    this.callGet('/navigation').then((r) => r.data);

  getSliders = () =>
    this.callGet('/sliders').then((r) => r.data);

  getFilterOptions = () =>
    this.callGet('/inventory/filters').then((r) => r.data);

  getFeaturedVehicles = () =>
    this.callGet('/vehicles/featured').then((r) => r.data);

  getVehicles = async (filters = {}) => {
    const activeFilters = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    const { data } = await this.callGet('/vehicles', activeFilters);
    return Array.isArray(data)
      ? data.map((v) => ({
          ...v,
          bodyType: v.bodyType || v.bodyStyle,
          sellPrice: v.sellPrice || v.sell_price,
        }))
      : data;
  };

  getWhyBuyFeatures = () =>
    this.callGet('/why-buy').then((r) => r.data);

  getContact = () =>
    this.callGet('/contact').then((r) => r.data);

  getSocialLinks = () =>
    this.callGet('/social-links').then((r) => r.data);

  getBusinessHours = () =>
    this.callGet('/business-hours').then((r) => r.data);

  getPageContent = (id) =>
    this.callGet(`/menu/${id}`).then((r) => r.data);

  getInventoryItem = (id) =>
    this.callGet(`/inventory/${id}`).then((r) => {
      const v = r.data?.data ?? r.data;
      return {
        ...v,
        bodyType: v.bodyType || v.bodyStyle,
        sellPrice: v.sellPrice || v.sell_price,
      };
    });

  getContactForm = () =>
    this.callGet('/contact-form').then((r) => r.data);

  submitLoanApplication = (fields) =>
    this.callPost('/loan-application/submit', {
      user_id: siteConfig.userId,
      website_domain: window.location.hostname,
      ...fields,
    }).then((r) => r.data);

  submitContactQuery = (fields) =>
    this.callPost('/contact-form/submit', {
      user_id: siteConfig.userId,
      website_domain: window.location.hostname,
      fields,
    }).then((r) => r.data);
}

export const apiService = new ServiceWrapper();
